import { en } from "./en.js";
import { formatGlossaryTerm, GLOSSARY, type GlossaryTerm } from "./glossary.js";
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  type Locale,
  type LocaleStorageLike,
  SUPPORTED_LOCALES,
  type TranslationParams,
  type TranslationTree,
} from "./types.js";
import { zhCN } from "./zh-CN.js";

export {
  DEFAULT_LOCALE,
  GLOSSARY,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  formatGlossaryTerm,
  type GlossaryTerm,
  type Locale,
  type LocaleStorageLike,
  type TranslationParams,
  type TranslationTree,
};

export const translations = {
  "zh-CN": zhCN,
  en,
} as const satisfies Record<Locale, TranslationTree>;

type DotPrefix<Prefix extends string, Value> = Value extends string
  ? Prefix
  : Value extends TranslationTree
    ? `${Prefix}.${TranslationKeyFor<Value>}`
    : never;

type TranslationKeyFor<Tree extends TranslationTree> = {
  [Key in keyof Tree & string]: DotPrefix<Key, Tree[Key]>;
}[keyof Tree & string];

export type TranslationKey = TranslationKeyFor<typeof zhCN>;

export interface ResolveLocaleOptions {
  currentUrl?: string | URL;
  search?: string;
  navigatorLanguages?: readonly string[];
  storage?: LocaleStorageLike | null;
}

export function resolveLocale(options: ResolveLocaleOptions = {}): Locale {
  const storage = options.storage ?? getBrowserStorage();
  const pathLocale = readLocaleFromPath(options);
  if (pathLocale) {
    writeStoredLocale(storage, pathLocale);
    return pathLocale;
  }

  const urlLocale = readLocaleFromSearch(options);
  if (urlLocale) {
    writeStoredLocale(storage, urlLocale);
    return urlLocale;
  }

  const storedLocale = readStoredLocale(storage);
  if (storedLocale) {
    return storedLocale;
  }

  for (const language of readNavigatorLanguages(options)) {
    const locale = normalizeLocale(language);
    if (locale) {
      return locale;
    }
  }

  return DEFAULT_LOCALE;
}

export function normalizeLocale(
  value: string | null | undefined,
): Locale | null {
  const normalized = value?.trim().replace("_", "-").toLowerCase();
  if (!normalized) {
    return null;
  }
  if (
    normalized === "zh" ||
    normalized === "zh-cn" ||
    normalized === "zh-hans" ||
    normalized.startsWith("zh-hans-")
  ) {
    return "zh-CN";
  }
  if (normalized === "en" || normalized.startsWith("en-")) {
    return "en";
  }
  return null;
}

export function readStoredLocale(
  storage: LocaleStorageLike | null = getBrowserStorage(),
): Locale | null {
  if (!storage) {
    return null;
  }
  try {
    return normalizeLocale(storage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function writeStoredLocale(
  storage: LocaleStorageLike | null,
  locale: Locale,
): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage can be unavailable in private mode; locale still resolves for this page.
  }
}

export function translate(
  locale: Locale,
  key: TranslationKey,
  params: TranslationParams = {},
): string {
  const template = lookupTranslation(translations[locale], key) ?? key;
  return interpolate(template, params);
}

export function getTranslationKeyDiff(
  left: TranslationTree,
  right: TranslationTree,
): string[] {
  const leftKeys = new Set(flattenTranslationKeys(left));
  const rightKeys = new Set(flattenTranslationKeys(right));
  return [...leftKeys, ...rightKeys]
    .filter((key) => !leftKeys.has(key) || !rightKeys.has(key))
    .sort();
}

export function flattenTranslationKeys(
  tree: TranslationTree,
  prefix = "",
): string[] {
  return Object.entries(tree).flatMap(([key, value]) => {
    const nextKey = prefix.length > 0 ? `${prefix}.${key}` : key;
    return typeof value === "string"
      ? [nextKey]
      : flattenTranslationKeys(value, nextKey);
  });
}

function readLocaleFromSearch(options: ResolveLocaleOptions): Locale | null {
  const search = options.search ?? readSearchFromUrl(options.currentUrl);
  if (!search) {
    return null;
  }
  return normalizeLocale(new URLSearchParams(search).get("lang"));
}

function readLocaleFromPath(options: ResolveLocaleOptions): Locale | null {
  const pathname = readPathnameFromUrl(options.currentUrl);
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  return normalizeLocale(firstSegment);
}

function readSearchFromUrl(currentUrl: string | URL | undefined): string {
  if (currentUrl instanceof URL) {
    return currentUrl.search;
  }
  if (typeof currentUrl === "string") {
    try {
      return new URL(currentUrl, "https://xiuxian.local").search;
    } catch {
      return "";
    }
  }
  return typeof window === "undefined" ? "" : window.location.search;
}

function readPathnameFromUrl(currentUrl: string | URL | undefined): string {
  if (currentUrl instanceof URL) {
    return currentUrl.pathname;
  }
  if (typeof currentUrl === "string") {
    try {
      return new URL(currentUrl, "https://xiuxian.local").pathname;
    } catch {
      return "";
    }
  }
  return typeof window === "undefined" ? "" : window.location.pathname;
}

function readNavigatorLanguages(
  options: ResolveLocaleOptions,
): readonly string[] {
  if (options.navigatorLanguages) {
    return options.navigatorLanguages;
  }
  return typeof navigator === "undefined" ? [] : navigator.languages;
}

function lookupTranslation(tree: TranslationTree, key: string): string | null {
  let current: string | TranslationTree = tree;
  for (const segment of key.split(".")) {
    if (typeof current === "string") {
      return null;
    }
    const next: string | TranslationTree | undefined = current[segment];
    if (next === undefined) {
      return null;
    }
    current = next;
  }
  return typeof current === "string" ? current : null;
}

function interpolate(template: string, params: TranslationParams): string {
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

function getBrowserStorage(): LocaleStorageLike | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
